import React, { useState, useEffect, useContext, useMemo } from "react";

import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import {
  Assessment,
  AttachMoney,
  EventAvailable,
  FilterList,
  Forward,
  History,
  Instagram,
  PeopleAlt,
  QueryBuilder,
  SaveAlt,
  ShoppingCart,
  Timeline,
  WhatsApp,
  Facebook,
} from "@material-ui/icons";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import useDashboard from "../../hooks/useDashboard";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";

  return {
    mainContainer: {
      background: theme.palette.fancyBackground || theme.palette.background.default,
      paddingBottom: theme.spacing(3),
    },
    // Nova classe aplicada para gerenciar a rolagem vertical abaixo do cabeçalho principal
    mainContainerScroll: {
      maxHeight: "calc(100vh - 220px)", // Ajusta dinamicamente deduzindo o topo da página
      overflowY: "auto",
      paddingRight: theme.spacing(1),
      "&::-webkit-scrollbar": {
        width: "8px"
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        borderRadius: "4px"
      }
    },
    pageShell: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
    },
    heroCard: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 24,
      padding: theme.spacing(3),
      background: isLight
        ? "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(242,246,255,0.98) 100%)"
        : "linear-gradient(135deg, rgba(37,37,37,0.96) 0%, rgba(22,22,22,0.98) 100%)",
      boxShadow: isLight
        ? "0 24px 60px rgba(31, 41, 55, 0.08)"
        : "0 24px 60px rgba(0, 0, 0, 0.35)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
      "&::before": {
        content: '""',
        position: "absolute",
        top: -120,
        right: -80,
        width: 280,
        height: 280,
        borderRadius: "50%",
        background: isLight
          ? "radial-gradient(circle, rgba(25,118,210,0.16) 0%, rgba(25,118,210,0) 70%)"
          : "radial-gradient(circle, rgba(144,202,249,0.16) 0%, rgba(144,202,249,0) 70%)",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -100,
        left: -60,
        width: 240,
        height: 240,
        borderRadius: "50%",
        background: isLight
          ? "radial-gradient(circle, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0) 70%)"
          : "radial-gradient(circle, rgba(129,199,132,0.12) 0%, rgba(129,199,132,0) 70%)",
      },
    },
    heroContent: {
      position: "relative",
      zIndex: 1,
    },
    heroTopRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    heroBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
      borderRadius: 999,
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: 0.3,
      background: isLight ? "rgba(25,118,210,0.12)" : "rgba(144,202,249,0.12)",
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
      width: "fit-content",
    },
    heroTitle: {
      fontWeight: 800,
      fontSize: 30,
      lineHeight: 1.1,
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        fontSize: 24,
      },
    },
    heroSubtitle: {
      maxWidth: 760,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2.5),
    },
    heroMetaRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    heroPill: {
      borderRadius: 999,
      padding: theme.spacing(0.75, 1.5),
      background: isLight ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.06)",
      color: theme.palette.text.primary,
      fontSize: 13,
      fontWeight: 600,
    },
    heroActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1.5),
      justifyContent: "flex-end",
      [theme.breakpoints.down("sm")]: {
        justifyContent: "flex-start",
      },
    },
    actionButton: {
      minWidth: 140,
      borderRadius: 14,
      padding: theme.spacing(1.1, 2),
      boxShadow: "none",
    },
    filterCard: {
      borderRadius: 22,
      padding: theme.spacing(2.5),
      background: theme.palette.background.paper,
      boxShadow: isLight
        ? "0 18px 40px rgba(15, 23, 42, 0.06)"
        : "0 18px 40px rgba(0, 0, 0, 0.28)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "flex-start",
      },
    },
    sectionTitleWrap: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    sectionIcon: {
      width: 42,
      height: 42,
      borderRadius: 14,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: isLight ? "rgba(25,118,210,0.12)" : "rgba(144,202,249,0.12)",
      color: theme.palette.primary.main,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: 700,
    },
    sectionSubtitle: {
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    filtersGrid: {
      alignItems: "stretch",
    },
    filterActions: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(1),
      height: "100%",
      [theme.breakpoints.down("sm")]: {
        justifyContent: "flex-start",
      },
    },
    switchWrap: {
      marginRight: theme.spacing(0.5),
    },
    metricsGrid: {
      marginTop: theme.spacing(0.5),
    },
    metricCard: {
      height: "100%",
      borderRadius: 22,
      background: theme.palette.background.paper,
      boxShadow: isLight
        ? "0 18px 36px rgba(15, 23, 42, 0.06)"
        : "0 18px 36px rgba(0, 0, 0, 0.28)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
    },
    metricContent: {
      padding: theme.spacing(2.25),
      "&:last-child": {
        paddingBottom: theme.spacing(2.25),
      },
    },
    metricTopRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    metricIcon: {
      width: 46,
      height: 46,
      borderRadius: 16,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
    },
    metricLabel: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    metricValue: {
      fontSize: 28,
      lineHeight: 1,
      fontWeight: 800,
      letterSpacing: -0.4,
      marginBottom: theme.spacing(0.75),
    },
    metricSecondary: {
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    metricProgress: {
      marginTop: theme.spacing(1.25),
      height: 8,
      borderRadius: 999,
      backgroundColor: isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.08)",
    },
    resultsCard: {
      borderRadius: 22,
      background: theme.palette.background.paper,
      boxShadow: isLight
        ? "0 20px 42px rgba(15, 23, 42, 0.06)"
        : "0 20px 42px rgba(0, 0, 0, 0.28)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
      overflow: "hidden",
    },
    resultsHeader: {
      padding: theme.spacing(2.5, 2.5, 2),
      borderBottom: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
    },
    resultsBody: {
      padding: theme.spacing(2.5),
      maxHeight: "68vh",
      overflow: "auto",
      ...theme.scrollbarStylesSoftBig,
    },
    resultCard: {
      borderRadius: 20,
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
      background: isLight ? "#ffffff" : "rgba(255,255,255,0.02)",
      boxShadow: isLight
        ? "0 12px 26px rgba(15, 23, 42, 0.05)"
        : "0 12px 26px rgba(0, 0, 0, 0.18)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isLight
          ? "0 18px 32px rgba(15, 23, 42, 0.08)"
          : "0 18px 32px rgba(0, 0, 0, 0.28)",
        borderColor: isLight ? "rgba(25,118,210,0.18)" : "rgba(144,202,249,0.18)",
      },
    },
    resultCardContent: {
      padding: theme.spacing(2.25),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      flex: 1,
      "&:last-child": {
        paddingBottom: theme.spacing(2.25),
      },
    },
    resultHeaderRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
    },
    resultTitleBlock: {
      minWidth: 0,
    },
    resultTitle: {
      fontWeight: 700,
      fontSize: 16,
      marginBottom: 4,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    resultSubtitle: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
    statusChip: {
      fontWeight: 700,
      borderRadius: 12,
    },
    quickMeta: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    metaChip: {
      borderRadius: 12,
      fontWeight: 600,
      background: isLight ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.06)",
      color: theme.palette.text.primary,
    },
    infoGrid: {
      rowGap: theme.spacing(1),
    },
    infoLabel: {
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
      fontWeight: 700,
      marginBottom: 4,
    },
    infoValue: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
    },
    lastMessageBox: {
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 16,
      background: isLight ? "rgba(15,23,42,0.04)" : "rgba(255,255,255,0.05)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.04)" : "rgba(255,255,255,0.05)"}`,
    },
    actionRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginTop: "auto",
      paddingTop: theme.spacing(0.5),
    },
    emptyState: {
      borderRadius: 22,
      padding: theme.spacing(6, 3),
      textAlign: "center",
      border: `1px dashed ${isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.14)"}`,
      background: isLight ? "rgba(15,23,42,0.02)" : "rgba(255,255,255,0.03)",
    },
    paginationCard: {
      borderRadius: 20,
      padding: theme.spacing(2),
      background: theme.palette.background.paper,
      boxShadow: isLight
        ? "0 16px 34px rgba(15, 23, 42, 0.05)"
        : "0 16px 34px rgba(0, 0, 0, 0.24)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)"}`,
    },
    paginationRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
      },
    },
    paginationInfo: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      fontWeight: 600,
    },
    pageSizeControl: {
      minWidth: 160,
    },
    skeletonWrap: {
      paddingTop: theme.spacing(1),
    },
  };
});

const defaultMetrics = {
  total: 0,
  closed: 0,
  open: 0,
  pending: 0,
  rated: 0,
  salesCount: 0,
  salesValue: 0,
  avgNps: 0,
  ratingRate: 0,
  closedRate: 0,
  salesRate: 0,
};

const Reports = () => {
  const classes = useStyles();
  const history = useHistory();
  const { getReport } = useDashboard();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState(defaultMetrics);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });

          if (active) {
            setOptions(data.contacts || []);
          }
        } catch (err) {
          if (active) {
            toastError(err);
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

      if (searchParam && searchParam.length >= 3) {
        fetchContacts();
      } else {
        setOptions([]);
        setLoading(false);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam]);

  const buildReportParams = (page, size) => ({
    searchParam,
    contactId: selectedContactId,
    whatsappId: JSON.stringify(selectedWhatsapp),
    users: JSON.stringify(userIds),
    queueIds: JSON.stringify(queueIds),
    status: JSON.stringify(selectedStatus),
    dateFrom,
    dateTo,
    page,
    pageSize: size,
    onlyRated: onlyRated ? "true" : "false",
  });

  const getTranslation = (key, fallback) => {
    const translation = i18n.t(key);
    return translation !== key ? translation : fallback;
  };

  const safeArray = (value) => (Array.isArray(value) ? value : []);

  const normalizeCurrencyValue = (value) => {
    if (value === undefined || value === null || value === "") return 0;
    if (typeof value === "number") return value;

    const normalized = String(value)
      .replace(/\s/g, "")
      .replace(/R\$/gi, "")
      .replace(/\./g, "")
      .replace(/,/g, ".")
      .replace(/[^0-9.-]/g, "");

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(normalizeCurrencyValue(value));

  const formatSupportTime = (timeString) => {
    if (!timeString) return "-";
    if (timeString === "0") return "0min";

    const compact = String(timeString)
      .replace(/\s*d,?\s*/i, "d ")
      .replace(/\s*hr?s?\s*/i, "h ")
      .replace(/\s*e\s*/i, " ")
      .replace(/\s*m(in)?\s*/i, "min")
      .replace(/\s+/g, " ")
      .trim();

    return compact || timeString;
  };

  const formatDateTimeValue = (value) => {
    if (!value) return "-";
    if (typeof value === "string" && value.includes("/")) return value;

    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value;
  };

  const formatDateOnly = (value) => {
    if (!value) return "-";
    const parsed = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
    if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
    return value;
  };

  const getStatusMeta = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized.includes("close") || normalized.includes("fech")) {
      return {
        label: status || "Fechado",
        background: "rgba(76, 175, 80, 0.14)",
        color: "#2e7d32",
      };
    }

    if (normalized.includes("pend")) {
      return {
        label: status || "Pendente",
        background: "rgba(255, 152, 0, 0.16)",
        color: "#ef6c00",
      };
    }

    return {
      label: status || "Em atendimento",
      background: "rgba(33, 150, 243, 0.16)",
      color: "#1565c0",
    };
  };

  const computeMetrics = (reportTickets, totalFromApi) => {
    const items = safeArray(reportTickets);

    const metrics = items.reduce(
      (acc, ticket) => {
        const normalizedStatus = String(ticket?.status || "").toLowerCase();
        const npsValue = Number(ticket?.NPS);
        const saleValue = normalizeCurrencyValue(ticket?.valorVenda);
        const hasSale = ticket?.finalizadoComVenda === true || saleValue > 0;

        if (normalizedStatus.includes("close") || normalizedStatus.includes("fech")) {
          acc.closed += 1;
        } else if (normalizedStatus.includes("pend")) {
          acc.pending += 1;
        } else {
          acc.open += 1;
        }

        if (ticket?.NPS !== undefined && ticket?.NPS !== null && ticket?.NPS !== "") {
          acc.rated += 1;
          if (!Number.isNaN(npsValue)) {
            acc.npsAccumulator += npsValue;
          }
        }

        if (hasSale) {
          acc.salesCount += 1;
          acc.salesValue += saleValue;
        }

        return acc;
      },
      {
        total: Number(totalFromApi) || items.length,
        closed: 0,
        open: 0,
        pending: 0,
        rated: 0,
        salesCount: 0,
        salesValue: 0,
        npsAccumulator: 0,
      }
    );

    const totalBase = metrics.total || items.length || 0;
    const avgNps = metrics.rated > 0 ? metrics.npsAccumulator / metrics.rated : 0;

    return {
      total: totalBase,
      closed: metrics.closed,
      open: metrics.open,
      pending: metrics.pending,
      rated: metrics.rated,
      salesCount: metrics.salesCount,
      salesValue: metrics.salesValue,
      avgNps,
      ratingRate: totalBase ? (metrics.rated / totalBase) * 100 : 0,
      closedRate: totalBase ? (metrics.closed / totalBase) * 100 : 0,
      salesRate: totalBase ? (metrics.salesCount / totalBase) * 100 : 0,
    };
  };

  const exportarGridParaExcel = async () => {
    setLoading(true);

    try {
      const data = await getReport(buildReportParams(1, 9999999));
      const reportTickets = safeArray(data?.tickets);

      const ticketsData = reportTickets.map((ticket) => {
        const formatDate = (dateString) => {
          if (!dateString || dateString === null) {
            return { date: "", time: "" };
          }

          if (String(dateString).includes("/") && String(dateString).includes(" ")) {
            const [datePart, timePart] = String(dateString).split(" ");
            return {
              date: datePart,
              time: timePart,
            };
          }

          const date = new Date(dateString);
          if (Number.isNaN(date.getTime())) {
            return { date: "", time: "" };
          }

          return {
            date: date.toLocaleDateString("pt-BR"),
            time: date.toLocaleTimeString("pt-BR"),
          };
        };

        const createdAtFormatted = formatDate(ticket.createdAt);
        const closedAtFormatted = formatDate(ticket.closedAt);

        return {
          id: ticket.id,
          Conexão: ticket.whatsappName,
          Contato: ticket.contactName,
          Usuário: ticket.userName,
          Fila: ticket.queueName,
          Status: ticket.status,
          ÚltimaMensagem: ticket.lastMessage,
          DataAbertura: createdAtFormatted.date,
          HoraAbertura: createdAtFormatted.time,
          DataFechamento: closedAtFormatted.date,
          HoraFechamento: closedAtFormatted.time,
          TempoDeAtendimento: formatSupportTime(ticket.supportTime),
          nps: ticket.NPS || "",
          ValorDaVenda:
            ticket.valorVenda !== undefined && ticket.valorVenda !== null
              ? ticket.valorVenda
              : "-",
          MotivoDaNaoVenda: ticket.motivoNaoVenda || "-",
          FinalizadoComVenda:
            ticket.finalizadoComVenda === true
              ? "Sim"
              : ticket.finalizadoComVenda === false
              ? "Não"
              : "-",
        };
      });

      const formatDateBR = (dateStr) => {
        if (!dateStr) {
          return getTranslation("reports.exportExcel.notInformed", "Não informado");
        }

        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      };

      const translations = {
        title: getTranslation("reports.exportExcel.title", "VERTICALTECH Relatórios"),
        startDate: getTranslation("reports.exportExcel.startDate", "Data inicial"),
        endDate: getTranslation("reports.exportExcel.endDate", "Data final"),
        columns: {
          id: getTranslation("reports.exportExcel.columns.id", "ID"),
          connection: getTranslation("reports.exportExcel.columns.connection", "Conexão"),
          contact: getTranslation("reports.exportExcel.columns.contact", "Contato"),
          user: getTranslation("reports.exportExcel.columns.user", "Usuário"),
          queue: getTranslation("reports.exportExcel.columns.queue", "Fila"),
          status: getTranslation("reports.exportExcel.columns.status", "Status"),
          lastMessage: getTranslation(
            "reports.exportExcel.columns.lastMessage",
            "Última Mensagem"
          ),
          openDate: getTranslation("reports.exportExcel.columns.openDate", "Data Abertura"),
          openTime: getTranslation("reports.exportExcel.columns.openTime", "Hora Abertura"),
          closeDate: getTranslation(
            "reports.exportExcel.columns.closeDate",
            "Data Fechamento"
          ),
          closeTime: getTranslation(
            "reports.exportExcel.columns.closeTime",
            "Hora Fechamento"
          ),
          supportTime: getTranslation(
            "reports.exportExcel.columns.supportTime",
            "Tempo de Atendimento"
          ),
          nps: getTranslation("reports.exportExcel.columns.nps", "NPS"),
          valorVenda: getTranslation(
            "reports.exportExcel.columns.valorVenda",
            "Valor da Venda"
          ),
          motivoNaoVenda: getTranslation(
            "reports.exportExcel.columns.motivoNaoVenda",
            "Motivo da Não Venda"
          ),
          finalizadoComVenda: getTranslation(
            "reports.exportExcel.columns.finalizadoComVenda",
            "Finalizado com Venda"
          ),
        },
      };

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        [translations.title],
        [`${translations.startDate}: ${formatDateBR(dateFrom)}`],
        [`${translations.endDate}: ${formatDateBR(dateTo)}`],
        [],
        [
          translations.columns.id,
          translations.columns.connection,
          translations.columns.contact,
          translations.columns.user,
          translations.columns.queue,
          translations.columns.status,
          translations.columns.lastMessage,
          translations.columns.openDate,
          translations.columns.openTime,
          translations.columns.closeDate,
          translations.columns.closeTime,
          translations.columns.supportTime,
          translations.columns.nps,
          translations.columns.valorVenda,
          translations.columns.motivoNaoVenda,
          translations.columns.finalizadoComVenda,
        ],
      ]);

      XLSX.utils.sheet_add_json(ws, ticketsData, {
        origin: "A6",
        skipHeader: true,
      });

      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { c: 0, r: 0 }, e: { c: 12, r: 0 } });

      ws["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 25 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 8 },
        { wch: 15 },
        { wch: 20 },
        { wch: 18 },
      ];

      ws["!rows"] = [
        { hpt: 25 },
        { hpt: 20 },
        { hpt: 20 },
        { hpt: 10 },
        { hpt: 25 },
      ];

      const range = XLSX.utils.decode_range(ws["!ref"]);

      for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum += 1) {
        for (let colNum = range.s.c; colNum <= range.e.c; colNum += 1) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          if (!ws[cellAddress]) continue;

          if (rowNum === 0) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          } else if (rowNum === 1 || rowNum === 2) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 12 },
              fill: { fgColor: { rgb: "E7E6E6" } },
            };
          } else if (rowNum === 4) {
            ws[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "70AD47" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendimentos");
      XLSX.writeFile(wb, "relatorio-de-atendimentos.xlsx", {
        cellStyles: true,
        bookType: "xlsx",
      });
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (targetPage = 1) => {
    setLoading(true);
    setMetricsLoading(true);

    try {
      const [pagedData, summaryData] = await Promise.all([
        getReport(buildReportParams(targetPage, pageSize)),
        getReport(buildReportParams(1, 9999999)),
      ]);

      const currentTickets = safeArray(pagedData?.tickets);
      const total = Number(pagedData?.totalTickets?.total || 0);
      const summaryTickets = safeArray(summaryData?.tickets);
      const summaryTotal = Number(summaryData?.totalTickets?.total || summaryTickets.length);

      setTickets(currentTickets);
      setTotalTickets(total || summaryTotal || currentTickets.length);
      setPageNumber(targetPage);
      setSummaryMetrics(computeMetrics(summaryTickets, summaryTotal));
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    handleFilter(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const handleSelectedUsers = (selecteds) => {
    setUserIds((selecteds || []).map((t) => t.id));
  };

  const handleSelectedWhatsapps = (selecteds) => {
    setSelectedWhatsapp((selecteds || []).map((t) => t.id));
  };

  const handleSelectedStatus = (selecteds) => {
    setSelectedStatus((selecteds || []).map((t) => t.status));
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
      default:
        return <PeopleAlt color="action" />;
    }
  };

  const renderOption = (option) => {
    if (option.number) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: 10,
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name} - {option.number}
          </Typography>
        </>
      );
    }

    return `${i18n.t("newTicketModal.add")} ${option.name}`;
  };

  const handleSelectOption = (e, newValue) => {
    if (!newValue || !newValue.id) {
      setSelectedContactId(null);
      return;
    }

    setSelectedContactId(newValue.id);
    setSearchParam("");
  };

  const renderOptionLabel = (option) => {
    if (typeof option === "string") return option;
    if (option.number) return `${option.name} - ${option.number}`;
    return `${option.name || ""}`;
  };

  const filter = createFilterOptions({
    trim: true,
  });

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderContactAutocomplete = () => (
    <Autocomplete
      fullWidth
      options={options}
      loading={loading && searchParam.length >= 3}
      clearOnBlur
      autoHighlight
      freeSolo
      size="small"
      clearOnEscape
      getOptionLabel={renderOptionLabel}
      renderOption={renderOption}
      filterOptions={createAddContactOption}
      onChange={(e, newValue) => handleSelectOption(e, newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={i18n.t("newTicketModal.fieldLabel")}
          variant="outlined"
          size="small"
          onChange={(e) => setSearchParam(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading && searchParam.length >= 3 ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );

  const periodLabel = useMemo(() => {
    const from = formatDateOnly(dateFrom);
    const to = formatDateOnly(dateTo);
    const days = Math.max(moment(dateTo).diff(moment(dateFrom), "days") + 1, 1);
    return `${from} até ${to} • ${days} dia${days > 1 ? "s" : ""}`;
  }, [dateFrom, dateTo]);

  const metricCards = useMemo(
    () => [
      {
        key: "total",
        label: "Atendimentos no período",
        value: summaryMetrics.total,
        secondary: `${summaryMetrics.closed} concluídos • ${summaryMetrics.open + summaryMetrics.pending} ativos`,
        progress: 100,
        icon: <Assessment fontSize="small" />,
        background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
      },
      {
        key: "closed",
        label: "Taxa de conclusão",
        value: `${summaryMetrics.closedRate.toFixed(0)}%`,
        secondary: `${summaryMetrics.closed} atendimentos finalizados`,
        progress: Math.min(summaryMetrics.closedRate, 100),
        icon: <EventAvailable fontSize="small" />,
        background: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)",
      },
      {
        key: "rating",
        label: "Tickets avaliados",
        value: summaryMetrics.rated,
        secondary: `${summaryMetrics.ratingRate.toFixed(0)}% do total com NPS`,
        progress: Math.min(summaryMetrics.ratingRate, 100),
        icon: <Timeline fontSize="small" />,
        background: "linear-gradient(135deg, #6a1b9a 0%, #ab47bc 100%)",
      },
      {
        key: "avgNps",
        label: "NPS médio",
        value: summaryMetrics.rated ? summaryMetrics.avgNps.toFixed(1) : "-",
        secondary: summaryMetrics.rated
          ? `${summaryMetrics.rated} avaliações consideradas`
          : "Sem avaliações no período",
        progress: summaryMetrics.rated ? Math.min(summaryMetrics.avgNps * 10, 100) : 0,
        icon: <QueryBuilder fontSize="small" />,
        background: "linear-gradient(135deg, #ef6c00 0%, #ffa726 100%)",
      },
      {
        key: "salesCount",
        label: "Conversões com venda",
        value: summaryMetrics.salesCount,
        secondary: `${summaryMetrics.salesRate.toFixed(0)}% dos atendimentos`,
        progress: Math.min(summaryMetrics.salesRate, 100),
        icon: <ShoppingCart fontSize="small" />,
        background: "linear-gradient(135deg, #00897b 0%, #26c6da 100%)",
      },
      {
        key: "salesValue",
        label: "Valor vendido",
        value: formatCurrency(summaryMetrics.salesValue),
        secondary: "Soma das vendas registradas no período",
        progress: summaryMetrics.salesCount > 0 ? 100 : 0,
        icon: <AttachMoney fontSize="small" />,
        background: "linear-gradient(135deg, #5d4037 0%, #8d6e63 100%)",
      },
    ],
    [summaryMetrics]
  );

  return (
    <MainContainer className={classes.mainContainer}>
      {openTicketMessageDialog && ticketOpen && (
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen.id}
        />
      )}

      <Title>{i18n.t("reports.title")}</Title>

      {/* Box contendo a rolagem estruturada para toda a área abaixo do título */}
      <Box className={classes.mainContainerScroll}>
        <div className={classes.pageShell}>
          <Paper className={classes.heroCard} elevation={0}>
            <div className={classes.heroContent}>
              <div className={classes.heroTopRow}>
                <div>
                  <div className={classes.heroBadge}>
                    <Assessment fontSize="small" />
                    Reports Intelligence
                  </div>
                  <Typography className={classes.heroTitle}>
                    Relatórios de coversas orientados por período
                  </Typography>
                  <Typography className={classes.heroSubtitle}>
                    Relatório premium com filtros rápidos, métricas consolidadas e cards de atendimento
                    para facilitar a análise de resultados.
                  </Typography>
                  <div className={classes.heroMetaRow}>
                    <span className={classes.heroPill}>Período: {periodLabel}</span>
                    <span className={classes.heroPill}>Usuário: {user?.name || "Equipe"}</span>
                    <span className={classes.heroPill}>Total atual: {summaryMetrics.total}</span>
                  </div>
                </div>

                <div className={classes.heroActions}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FilterList />}
                    className={classes.actionButton}
                    onClick={() => handleFilter(1)}
                  >
                    Atualizar dados
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SaveAlt />}
                    className={classes.actionButton}
                    onClick={exportarGridParaExcel}
                  >
                    Exportar Excel
                  </Button>
                </div>
              </div>
            </div>
          </Paper>

          <MainHeader style={{ display: "block" }}>
            <Paper className={classes.filterCard} elevation={0}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionTitleWrap}>
                  <span className={classes.sectionIcon}>
                    <FilterList />
                  </span>
                  <div>
                    <Typography className={classes.sectionTitle}>Filtros de análise</Typography>
                    <Typography className={classes.sectionSubtitle}>
                      Combine período, responsáveis, status, filas e canais para refinar os dados.
                    </Typography>
                  </div>
                </div>
                <Chip
                  label={onlyRated ? "Somente tickets avaliados" : "Todos os tickets"}
                  color={onlyRated ? "primary" : "default"}
                  variant={onlyRated ? "default" : "outlined"}
                />
              </div>

              <Grid container spacing={2} className={classes.filtersGrid}>
                <Grid item xs={12} md={4} lg={3}>
                  {renderContactAutocomplete()}
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <StatusFilter onFiltered={handleSelectedStatus} />
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <UsersFilter onFiltered={handleSelectedUsers} />
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <QueueSelectCustom
                    selectedQueueIds={queueIds}
                    onChange={(values) => setQueueIds(values)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    label={i18n.t("reports.startDate")}
                    type="date"
                    value={dateFrom}
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    label={i18n.t("reports.endDate")}
                    type="date"
                    value={dateTo}
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={8} lg={4}>
                  <div className={classes.filterActions}>
                    <FormControlLabel
                      className={classes.switchWrap}
                      control={
                        <Switch
                          color="primary"
                          checked={onlyRated}
                          onChange={() => setOnlyRated(!onlyRated)}
                        />
                      }
                      label={i18n.t("reports.buttons.onlyRated")}
                    />
                    <IconButton onClick={exportarGridParaExcel} aria-label="Exportar para Excel">
                      <SaveAlt />
                    </IconButton>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FilterList />}
                      onClick={() => handleFilter(1)}
                    >
                      {i18n.t("reports.buttons.filter")}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </MainHeader>

          <Grid container spacing={2} className={classes.metricsGrid}>
            {metricCards.map((metric) => (
              <Grid item xs={12} sm={6} lg={4} xl={2} key={metric.key}>
                <Card className={classes.metricCard} elevation={0}>
                  <CardContent className={classes.metricContent}>
                    <div className={classes.metricTopRow}>
                      <div>
                        <Typography className={classes.metricLabel}>{metric.label}</Typography>
                        <Typography className={classes.metricValue}>{metric.value}</Typography>
                      </div>
                      <span className={classes.metricIcon} style={{ background: metric.background }}>
                        {metric.icon}
                      </span>
                    </div>
                    <Typography className={classes.metricSecondary}>{metric.secondary}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Number.isFinite(metric.progress) ? metric.progress : 0}
                      className={classes.metricProgress}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper className={classes.resultsCard} elevation={0}>
            <div className={classes.resultsHeader}>
              <div className={classes.sectionHeader} style={{ marginBottom: 0 }}>
                <div className={classes.sectionTitleWrap}>
                  <span className={classes.sectionIcon}>
                    <Timeline />
                  </span>
                  <div>
                    <Typography className={classes.sectionTitle}>Lista de resultados</Typography>
                    <Typography className={classes.sectionSubtitle}>
                      Visual em cards com dados principais, última mensagem e atalhos rápidos.
                    </Typography>
                  </div>
                </div>
                <Chip
                  label={`${tickets.length} exibidos nesta página • ${totalTickets} no total`}
                  color="primary"
                  variant="outlined"
                />
              </div>
              {(loading || metricsLoading) && <LinearProgress style={{ marginTop: 16, borderRadius: 999 }} />}
            </div>

            <div className={classes.resultsBody}>
              {loading ? (
                <div className={classes.skeletonWrap}>
                  <TableRowSkeleton avatar columns={4} />
                  <TableRowSkeleton avatar columns={4} />
                  <TableRowSkeleton avatar columns={4} />
                </div>
              ) : tickets.length === 0 ? (
                <div className={classes.emptyState}>
                  <Assessment style={{ fontSize: 42, opacity: 0.7, marginBottom: 12 }} />
                  <Typography variant="h6" gutterBottom>
                    Nenhum atendimento encontrado
                  </Typography>
                  <Typography color="textSecondary">
                    Ajuste os filtros ou selecione outro período para visualizar os resultados.
                  </Typography>
                </div>
              ) : (
                <Grid container spacing={2}>
                  {tickets.map((ticket) => {
                    const statusMeta = getStatusMeta(ticket?.status);
                    const saleRegistered =
                      ticket?.finalizadoComVenda === true || normalizeCurrencyValue(ticket?.valorVenda) > 0;

                    return (
                      <Grid item xs={12} md={6} xl={4} key={ticket.id}>
                        <Card className={classes.resultCard} elevation={0}>
                          <CardContent className={classes.resultCardContent}>
                            <div className={classes.resultHeaderRow}>
                              <div className={classes.resultTitleBlock}>
                                <Typography className={classes.resultTitle}>
                                  {ticket?.contactName || "Contato não informado"}
                                </Typography>
                                <Typography className={classes.resultSubtitle}>
                                  <span>#{ticket?.id}</span>
                                  <span>•</span>
                                  <span>{ticket?.whatsappName || "Canal não informado"}</span>
                                </Typography>
                              </div>
                              <Chip
                                label={statusMeta.label}
                                size="small"
                                className={classes.statusChip}
                                style={{
                                  backgroundColor: statusMeta.background,
                                  color: statusMeta.color,
                                }}
                              />
                            </div>

                            <div className={classes.quickMeta}>
                              <Chip
                                size="small"
                                label={`Usuário: ${ticket?.userName || "-"}`}
                                className={classes.metaChip}
                              />
                              <Chip
                                size="small"
                                label={`Fila: ${ticket?.queueName || "-"}`}
                                className={classes.metaChip}
                              />
                              <Chip
                                size="small"
                                label={`Carteira: ${ticket?.walletName || "-"}`}
                                className={classes.metaChip}
                              />
                            </div>

                            <Grid container spacing={2} className={classes.infoGrid}>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>Abertura</Typography>
                                <Typography className={classes.infoValue}>
                                  {formatDateTimeValue(ticket?.createdAt)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>Fechamento</Typography>
                                <Typography className={classes.infoValue}>
                                  {formatDateTimeValue(ticket?.closedAt)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>Tempo de atendimento</Typography>
                                <Typography className={classes.infoValue}>
                                  {formatSupportTime(ticket?.supportTime)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>NPS</Typography>
                                <Typography className={classes.infoValue}>
                                  {ticket?.NPS !== undefined && ticket?.NPS !== null && ticket?.NPS !== ""
                                    ? ticket.NPS
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>Valor da venda</Typography>
                                <Typography className={classes.infoValue}>
                                  {ticket?.valorVenda !== undefined && ticket?.valorVenda !== null
                                    ? formatCurrency(ticket?.valorVenda)
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography className={classes.infoLabel}>Fechado com venda</Typography>
                                <Typography className={classes.infoValue}>
                                  {saleRegistered ? "Sim" : ticket?.finalizadoComVenda === false ? "Não" : "-"}
                                </Typography>
                              </Grid>
                            </Grid>

                            <div className={classes.lastMessageBox}>
                              <Typography className={classes.infoLabel}>Última mensagem</Typography>
                              <Typography variant="body2" color="textPrimary">
                                {ticket?.lastMessage || "Nenhuma mensagem registrada."}
                              </Typography>
                            </div>

                            {ticket?.motivoNaoVenda ? (
                              <div>
                                <Typography className={classes.infoLabel}>Motivo da não venda</Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {ticket.motivoNaoVenda}
                                </Typography>
                              </div>
                            ) : null}

                            <Divider />

                            <div className={classes.actionRow}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<History />}
                                onClick={() => {
                                  setOpenTicketMessageDialog(true);
                                  setTicketOpen(ticket);
                                }}
                              >
                                Logs
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<Forward />}
                                onClick={() => {
                                  history.push(`/tickets/${ticket.uuid}`);
                                }}
                              >
                                Abrir ticket
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </div>
          </Paper>

          <Paper className={classes.paginationCard} elevation={0}>
            <div className={classes.paginationRow}>
              <div>
                <Typography className={classes.paginationInfo}>
                  Página {pageNumber} de {Math.max(Math.ceil(totalTickets / pageSize), 1)}
                </Typography>
                <Pagination
                  count={Math.max(Math.ceil(totalTickets / pageSize), 1)}
                  page={pageNumber}
                  onChange={(event, value) => handleFilter(value)}
                  color="primary"
                  size="medium"
                  style={{ marginTop: 10 }}
                />
              </div>

              <FormControl margin="dense" variant="outlined" className={classes.pageSizeControl}>
                <InputLabel>{i18n.t("tickets.search.ticketsPerPage")}</InputLabel>
                <Select
                  labelId="dialog-select-prompt-label"
                  id="dialog-select-prompt"
                  name="pageSize"
                  value={pageSize}
                  onChange={(e) => {
                    setPageNumber(1);
                    setPageSize(e.target.value);
                  }}
                  label={i18n.t("tickets.search.ticketsPerPage")}
                  fullWidth
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </div>
          </Paper>
        </div>
      </Box>
    </MainContainer>
  );
};

export default Reports;